"use client";
import React, { useEffect, useState, useMemo } from "react";
import { message } from "antd";
import { Link, usePathname } from "@/navigation";
import { useCategory } from "@/context/CategoriesContext";
import { useTranslations } from "next-intl";
import { contactInfo, contactObj } from "@/lib/contacts";
import useIsMobile from "@/lib/hooks/useIsMobile";
import clsx from "clsx";
import Collapse from "../Collapse";
import { subscribeEmail } from "@/lib/api/subscribe";
import { 
  InstagramLogo, 
  YoutubeLogo,
  LinkedinLogo,
  FacebookLogo 
} from "@phosphor-icons/react";
import FooterContactForm from "./FooterContactForm";

const Footer = () => {
  const { categories } = useCategory();
  const t = useTranslations();
  const pathname = usePathname();
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const isMobile = useIsMobile();

  // 判断是否为首页 (考虑到多语言情况)
  // const isHomePage = pathname === "/" || pathname.match(/^\/[a-z]{2}$/) || pathname.match(/^\/[a-z]{2}\/?$/);
  // const containerClass = isHomePage ? "w-[85vw] mx-auto max-md:container" : "container mx-auto";
  const containerClass = "container mx-auto";

  const [isOpen, setIsOpen] = useState({
    quickLinks: true,
    products: true,
    custom: true,
    aboutUs: true,
    contactUs: true,
  });

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    if (isMobile) {
      setIsOpen({
        quickLinks: false,
        products: false,
        custom: false,
        aboutUs: false,
        contactUs: false,
      });
    } else {
      setIsOpen({
        quickLinks: true,
        products: true,
        custom: true,
        aboutUs: true,
        contactUs: true,
      });
    }
  }, [isMobile]);

  const toggleOpen = (key: keyof typeof isOpen) => {
    setIsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const subscribe = async (value: string) => {
    try {
      if (!value) return;
      setSubscribeLoading(true);
      await subscribeEmail({ email: value, subscribe_type: 3 });
      message.success(t("message.subscribeSuccess"));
    } catch (e: any) {
      message.error(e.message || "Error");
    }
    setSubscribeLoading(false);
  };

  // Split categories for Products and Custom columns
  const allCategories = categories?.edges || [];
  
  // Find custom category and its children
  // Try to find by slug (case-insensitive) or by name/translation
  const customCategory = useMemo(() => {
    return allCategories.find(
      (item) => {
        const slug = item.node.slug?.toLowerCase();
        const name = item.node.name?.toLowerCase();
        const translationName = item.node.translation?.name?.toLowerCase();
        return slug === "custom" || name === "custom" || translationName === "custom";
      }
    );
  }, [allCategories]);
  
  // Products: all categories except custom
  const productCategories = useMemo(() => {
    return allCategories.filter(
      (item) => {
        const slug = item.node.slug?.toLowerCase();
        const name = item.node.name?.toLowerCase();
        const translationName = item.node.translation?.name?.toLowerCase();
        return slug !== "custom" && name !== "custom" && translationName !== "custom";
      }
    );
  }, [allCategories]);


  const quickLinks = [
    { name: "menu.applications", link: "/products/applications"},
    { name: "menu.aboutUs", link: "/about-us" },
    { name: "menu.blog", link: "/category/company-news" },
		{ name: "menu.calculator", link: "/service/calculator" },
		{ name: "menu.download", link: "/service/download" },
    { name: "menu.contactUs", link: "/contact-us" },
  ];

  return (
    <footer className="w-full bg-mainColor pt-[50px] pb-[16px] max-md:pb-[90px] text-white">
      {/* Stay in Touch Section */}
      <div className={clsx(containerClass, "mb-10 text-center")}>
        <h2 className="text-3xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-5">{t("footer.Touch")}</h2>
        <p className="text-base xl:text-lg mb-5 text-white/80">{t("footer.SubscribeNewsletter")}</p>
        <div className="max-w-[600px] mx-auto">
          <SearchInput onSearch={subscribe} loading={subscribeLoading} />
        </div>
      </div>

      {/* Main Links Section */}
      <div className={clsx(containerClass, "grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8 mb-8")}>
        {/* Quick Links */}
        <div>
          <button
            type="button"
            aria-label={t("menu.Quick Iinks")}
            aria-expanded={isOpen.quickLinks}
            className="flex justify-between items-center mb-6 cursor-pointer md:cursor-default w-full text-left" 
            onClick={() => isMobile && toggleOpen('quickLinks')}
          >
            <span className="text-xl xl:text-2xl">{t("menu.Quick Iinks")}</span>
            {isMounted && <i className={clsx("md:hidden text-xl", !isOpen.quickLinks ? "ri-add-line" : "ri-subtract-fill")} aria-hidden="true"></i>}
          </button>
          <Collapse open={isOpen.quickLinks}>
            <ul className="space-y-3">
              {quickLinks.map((item, index) => (
                <li key={index}>
                  <Link href={item.link} className="text-white/80 hover:text-white text-base">
                   {t(item.name)}
                  </Link>
                </li>
              ))}
            </ul>
          </Collapse>
        </div>

        {/* Products */}
        <div>
          <button
            type="button"
            aria-label={t("menu.product")}
            aria-expanded={isOpen.products}
            className="flex justify-between items-center mb-6 cursor-pointer md:cursor-default w-full text-left" 
            onClick={() => isMobile && toggleOpen('products')}
          >
            <span className="text-xl xl:text-2xl">{t("menu.product")}</span>
            {isMounted && <i className={clsx("md:hidden text-xl", !isOpen.products ? "ri-add-line" : "ri-subtract-fill")} aria-hidden="true"></i>}
          </button>
          <Collapse open={isOpen.products}>
            <ul className="space-y-3">
              {productCategories.map((item) => (
                <li key={item.node.id}>
                  <Link href={"/products/" + item.node.slug} className="text-white/80 hover:text-white text-base">
                    {item?.node?.translation?.name || item?.node?.name}
                  </Link>
                </li>
              ))}
            </ul>
          </Collapse>
        </div>

        {/* Contact Us */}
        <div>
          <button
            type="button"
            aria-label={t("footer.Find-Blince")}
            aria-expanded={isOpen.contactUs}
            className="flex justify-between items-center mb-6 cursor-pointer md:cursor-default w-full text-left" 
            onClick={() => isMobile && toggleOpen('contactUs')}
          >
            <span className="text-xl xl:text-2xl">{t("footer.Find-Blince")}</span>
            {isMounted && <i className={clsx("md:hidden text-xl", !isOpen.contactUs ? "ri-add-line" : "ri-subtract-fill")} aria-hidden="true"></i>}
          </button>
          <Collapse open={isOpen.contactUs}>
            <ul className="space-y-3 text-base">
              <li className="text-white/80">
                {t('footer.Tel')} : 
                <a href={`tel:${contactInfo.phone}`} className="hover:text-white ml-1 text-white/80">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="text-white/80">
                {t('footer.WhatsApp')} : 
                <a 
                  href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`} 
                  className="hover:text-white ml-1 text-white/80"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {contactInfo.whatsapp}
                </a>
              </li>
              <li className="text-white/80">
                {t('footer.E-mail')} : 
                <a href={`mailto:${contactInfo.email}`} className="hover:text-white ml-1 text-white/80">
                  {contactInfo.email}
                </a>
              </li>
              <li className="text-white/80">
                {t('footer.Wechat')} : 
                <a 
                  href="weixin://" 
                  className="hover:text-white ml-1 text-white/80"
                >
                  {contactInfo.wechat}
                </a>
              </li>
              <li className="leading-relaxed text-white/80">
                {t('footer.Address')}:
                <span className="ml-1">{contactInfo.address}</span>
              </li>
            </ul>
          </Collapse>
        </div>

        {/* Contact Form */}
        <div className="md:col-span-1 lg:col-span-1">
          <FooterContactForm />
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={clsx(containerClass, "border-t border-white/50 pt-4")}>
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-base text-white/80">
            {t("footer.copyright")}
          </div>
          <div className="flex gap-4">
             {/* Social Icons */}
             <SocialIcon href={contactObj.Youtube} icon={<YoutubeLogo size={20} weight="fill" />} label="Visit our YouTube channel" />
             <SocialIcon href={contactObj.instagram} icon={<InstagramLogo size={20} weight="fill" />} label="Visit our Instagram page" />
             <SocialIcon href={contactObj.FaceBook} icon={<FacebookLogo size={20} weight="fill" />} label="Visit our Facebook page" />
             <SocialIcon href={contactObj.Linkedin} icon={<LinkedinLogo size={20} weight="fill" />} label="Visit our LinkedIn page" />
          </div>
        </div>
      </div>
    </footer>
  );
};

const SocialIcon = ({ href, icon, label }: { href: string; icon: React.ReactNode; label: string }) => (
  <a 
    href={href} 
    target="_blank" 
    rel="noopener noreferrer"
    aria-label={label}
    className="w-8 h-8 rounded-full bg-white text-mainColor flex items-center justify-center hover:bg-white/90 transition-colors"
  >
    <span aria-hidden="true">{icon}</span>
  </a>
);

function SearchInput({ onSearch, loading }: any) {
  const [searchValue, setSearchValue] = useState("");
  const t = useTranslations();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearch(searchValue);
    }
  };

  return (
    <div className="relative w-full">
      <input
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="w-full h-[50px] pl-6 pr-[60px] rounded-full border-none outline-none text-[#333] bg-white shadow-sm"
        placeholder={t("nav.Email") + " *"}
      />
      <button
        type="button"
        onClick={() => onSearch(searchValue)}
        disabled={loading}
        aria-label="Subscribe to newsletter"
        className="absolute right-1 top-1 bottom-1 w-[42px] h-[42px] bg-mainColor rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
      >
        {loading ? (
           <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true"></div>
        ) : (
          <i className="ri-arrow-right-up-line text-xl" aria-hidden="true"></i>
        )}
      </button>
    </div>
  );
}

export default Footer;
